import { ArrowLeft, BarChart3, ChevronRight, Gamepad2, Wand2, Library, User, Coins, Building2, Briefcase, Activity } from "lucide-react";

/* Misteck Site Stats — a public "what's live" transparency board.
   Curated usability (readiness) scores per feature across Misteck Play &
   Misteck Life. Every row deep-links straight into that feature, so this page
   doubles as a navigation hub. Percentages are honest, hand-set readiness values
   (100 = fully wired & usable end-to-end). */

const PLAY = {
  key: "play", title: "Misteck Play", tag: "Creative & Games", accent: "#8b5cf6",
  href: "/?zone=play&tab=command",
  features: [
    { key: "games", name: "Mist Games", sub: "Ball Snipe · Mist Miner · earn gems", pct: 100, Icon: Gamepad2, href: "/?zone=play&tab=command" },
    { key: "creations", name: "Mist Creations", sub: "Reel · R-System · Create · Music · Editor", pct: 100, Icon: Wand2, href: "/?hub=imagery_hub&tab=command" },
    { key: "stock", name: "Stock Library", sub: "Matrix Vault · graphics · clips · audio", pct: 100, Icon: Library, href: "/gallery" },
  ],
};

const LIFE = {
  key: "life", title: "Misteck Life", tag: "Profile · Career · Wealth · Business", accent: "#38bdf8",
  href: "/?zone=life&tab=command",
  features: [
    { key: "profile", name: "Profile", sub: "Identity · Settings · Security", pct: 100, Icon: User, href: "/?life=profile&tab=command" },
    { key: "wealth", name: "Wealth", sub: "Wallet · Mortgage · Rewards · Net worth", pct: 100, Icon: Coins, href: "/?life=wealth&tab=command" },
    { key: "business", name: "Business", sub: "Plans · Marketing · Ad Maker · Van Design", pct: 95, Icon: Building2, href: "/?life=business&tab=command" },
    { key: "career", name: "Career", sub: "Jobs · CV · Autopilot", pct: 90, Icon: Briefcase, href: "/?life=career&tab=command" },
    { key: "leisure", name: "Leisure", sub: "Lifestyle · Travel · Fun", pct: 90, Icon: Gamepad2, href: "/?life=leisure&tab=command" },
  ],
};

const barColor = (p) => (p >= 90 ? "#22c55e" : p >= 70 ? "#f59e0b" : "#ef4444");
const statusLabel = (p) => (p >= 100 ? "Live" : p >= 90 ? "Live" : p >= 70 ? "Beta" : "In progress");
const avg = (arr) => Math.round(arr.reduce((s, f) => s + f.pct, 0) / (arr.length || 1));

function Meter({ pct, accent }) {
  return (
    <div className="w-full h-2.5 rounded-full bg-white/10 overflow-hidden border border-black/40">
      <div className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${pct}%`, backgroundColor: accent || barColor(pct) }} />
    </div>
  );
}

function Section({ section }) {
  const overall = avg(section.features);
  return (
    <div className="space-y-3" data-testid={`stats-section-${section.key}`}>
      <button onClick={() => { window.location.href = section.href; }}
        data-testid={`stats-section-open-${section.key}`}
        className="w-full flex items-center gap-3 rounded-2xl border-2 border-black p-3.5 text-left shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
        style={{ backgroundColor: section.accent }}>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-black/60">{section.tag}</p>
          <p className="font-serif text-2xl leading-none text-black">{section.title}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono text-2xl font-black text-black leading-none" data-testid={`stats-section-pct-${section.key}`}>{overall}%</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-black/55">usable</p>
        </div>
        <ChevronRight size={20} className="text-black/50 shrink-0" />
      </button>

      <div className="space-y-2">
        {section.features.map((f) => (
          <button key={f.key} onClick={() => { window.location.href = f.href; }}
            data-testid={`stats-feature-${section.key}-${f.key}`}
            className="w-full flex items-center gap-3 rounded-2xl border-2 border-white/12 bg-white/[0.03] hover:bg-white/[0.06] px-3.5 py-3 text-left active:translate-y-[1px] transition-colors">
            <span className="w-10 h-10 shrink-0 rounded-xl border-2 border-white/15 grid place-items-center" style={{ backgroundColor: `${section.accent}22` }}>
              <f.Icon size={18} className="text-white" style={{ color: section.accent }} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-sans font-bold text-[15px] text-white leading-tight truncate">{f.name}</p>
                <span className="shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[7px] font-black uppercase tracking-widest border"
                  style={{ color: barColor(f.pct), borderColor: `${barColor(f.pct)}66` }}>{statusLabel(f.pct)}</span>
              </div>
              <p className="font-mono text-[9px] text-white/45 leading-snug mt-0.5 truncate">{f.sub}</p>
              <div className="mt-1.5 flex items-center gap-2">
                <Meter pct={f.pct} />
                <span className="font-mono text-[10px] font-black shrink-0 w-9 text-right" style={{ color: barColor(f.pct) }} data-testid={`stats-feature-pct-${section.key}-${f.key}`}>{f.pct}%</span>
              </div>
            </div>
            <ChevronRight size={18} className="text-white/30 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SiteStatsPage({ onBack, isFounder = false }) {
  const play = isFounder
    ? { ...PLAY, features: [...PLAY.features, { key: "intent", name: "Intent Dashboard", sub: "Founder analytics", pct: 100, Icon: BarChart3, href: "/?zone=play&tab=command" }] }
    : PLAY;
  const siteOverall = avg([...play.features, ...LIFE.features]);

  return (
    <div className="absolute inset-0 z-[54] flex flex-col bg-slate-950" data-testid="site-stats-page">
      <div className="flex items-center gap-2 border-b-2 border-white/70 px-4 py-3">
        <button onClick={onBack} data-testid="site-stats-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#00E5C0]">Misteck Site Stats</p>
          <h2 className="font-serif text-xl text-white">What's live</h2>
        </div>
        <div className="text-right">
          <p className="font-mono text-2xl font-black text-[#00E5C0] leading-none" data-testid="site-stats-overall">{siteOverall}%</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-white/45">overall</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar p-4 pb-28 space-y-6">
        <div className="flex items-start gap-2 rounded-2xl border-2 border-white/10 bg-white/[0.02] p-3" data-testid="site-stats-intro">
          <Activity size={15} className="text-[#00E5C0] shrink-0 mt-0.5" />
          <p className="font-mono text-[10px] leading-relaxed text-white/55">
            Live readiness across <span className="text-white">Misteck Play</span> &amp; <span className="text-white">Misteck Life</span>. Tap any feature to jump straight in.
          </p>
        </div>
        <Section section={play} />
        <Section section={LIFE} />
      </div>
    </div>
  );
}
